import { UvdRobotJob } from "gql/graphql";

// Documentation available at
// https://blue-ocean-robotics.atlassian.net/wiki/spaces/SIR/pages/2480537828/Tree+states+and+DB+jobs+mapping+to+Robot+Status
// https://blue-ocean-robotics.atlassian.net/wiki/spaces/SIR/pages/3611983873/Tree+states+and+DB+jobs+mapping+to+Robot+Status+seto+3.1.1
const ERROR = {
  any_emergency_button_pressed: "Emergency stop",
  emergency_reset_button: "Emergency stop",
  emergency_button_report: "Emergency stop",
  robot_stuck: "Robot stuck",
  robot_stuck_recovery: "Operator action needed",
  robot_free: "Operator action needed",
  maintenance_mode: "In maintenance mode",
  push_robot_mode: "Operator action needed",
  rebootable_error: "Fatal error, trying to resolve",
  fatal_error: "Fatal error",
  battery_waiting_to_shutdown: "Fatal error, waiting for shutdown",
  battery_shutting_down: "Fatal error, waiting for shutdown",
  emergency_button_waiting_release: "Emergency stop",
  emergency_button_waiting_reset: "Emergency stop",
  emergency_button_waiting_ack: "Emergency stop",
  lidar_blocked_blocked: "Robot stuck",
  lidar_blocked_controlling: "Robot stuck",
  lidar_blocked_free_with_timer: "Operator action needed",
  lidar_blocked_pushing: "Robot stuck",
  lidar_blocked_waiting_direction: "Operator action needed",
  rebootable_waiting_reboot: "Fatal error, trying to resolve",
  rebootable_rebooting: "Fatal error, trying to resolve",
  fatal_generic: "Fatal error",
  fatal_shutdown: "Fatal error, waiting for shutdown",
  needs_reset_button_press: "Operator action needed",
};

//LSBRI - Update required?
export const getChargingStatus = (robotJob: UvdRobotJob) => {
  const { state } = robotJob;

  switch (state) {
    case "awaiting_charging":
      return { charging: false, pluggedIn: true, label: "Awaiting charging" };
    case "start_charging":
      return { charging: false, pluggedIn: true, label: "Preparing charging" };
    case "charging":
      return { charging: true, pluggedIn: false, label: "Charging" };
    case "charged":
      return { charging: true, pluggedIn: false, label: null };
    case "fully_charged":
    case "charging_failed":
      return { charging: false, pluggedIn: true, label: null };
    default:
      return null;
  }
};

//LSBRI - Update required?
export const getIsDocked = (state: UvdRobotJob["state"]) => {
  switch (state) {
    case "awaiting_charging":
    case "start_charging":
    case "charging":
    case "charged":
    case "fully_charged":
    case "charging_failed":
      return true;
    default:
      return false;
  }
};

//LSBRI - Update required?
export const getIsDisinfecting = (state: UvdRobotJob["state"]) => {
  switch (state) {
    case "manual_disinfecting":
    case "stationary_disinfecting":
    case "disinfecting":
      return true;
    default:
      return false;
  }
};

export const getRobotStatus = (robotJob: UvdRobotJob) => {
  const { type, state } = robotJob;

  const dbType = type?.toLowerCase();
  const defaultStatusMsg = "No status available";

  if (
    state === "any_emergency_button_pressed" ||
    state === "emergency_reset_button" ||
    state === "emergency_button_report" ||
    state === "robot_stuck" ||
    state === "robot_stuck_recovery" ||
    state === "robot_free" ||
    state === "maintenance_mode" ||
    state === "push_robot_mode" ||
    state === "rebootable_error" ||
    state === "fatal_error" ||
    state === "any_emergency_button_pressed" ||
    state === "battery_waiting_to_shutdown" ||
    state === "battery_shutting_down" ||
    state === "emergency_button_waiting_release" ||
    state === "emergency_button_waiting_reset" ||
    state === "emergency_button_waiting_ack" ||
    state === "lidar_blocked_blocked" ||
    state === "lidar_blocked_controlling" ||
    state === "lidar_blocked_free_with_timer" ||
    state === "lidar_blocked_pushing" ||
    state === "lidar_blocked_waiting_direction" ||
    state === "maintenance_mode" ||
    state === "push_robot_mode" ||
    state === "rebootable_waiting_reboot" ||
    state === "rebootable_rebooting" ||
    state === "fatal_generic" ||
    state === "fatal_shutdown" ||
    state === "needs_reset_button_press"
  ) {
    return ERROR[state];
  }
  if (dbType === "") {
    switch (state) {
      case "idle":
        return "Idle";
      case "awaiting_charging":
      case "start_charging":
      case "charging":
      case "charged":
      case "fully_charged":
      case "charging_failed":
        return "Docked";
      case "undocking":
        return "Undocking";
      case "undocking_cancelled":
        return "Undocking cancelled";
      case "driving_manual":
        return "Driving - Remote controlled";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "go to position") {
    switch (state) {
      case "driving":
      case "autonomous_movement":
        return "Driving autonomously";
      case "driving_paused":
      case "paused":
        return "Driving paused";
      case "driving_manual":
      case "manual_control":
        return "Driving - Remote controlled";
      case "driving_failed":
      case "failed_autonomous_movement":
      case "failed_database_call":
        return "Driving failed";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "go to docking station") {
    switch (state) {
      case "driving":
        return "Driving to dock";
      case "driving_paused":
        return "Driving paused";
      case "driving_manual":
        return "Driving - Remote controlled";
      case "driving_failed":
        return "Driving failed";
      case "docking_cancelled":
        return "Docking cancelled";
      case "prepare_docking":
      case "docking":
        return "Docking";
      case "docking_failed":
        return "Docking failed";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "create map") {
    switch (state) {
      case "start_mapping":
      case "mapping":
        return "Scanning area";
      case "start_localization":
        return "Locating on map";
      case "map_align":
      case "map_review":
        return "Map editing";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "shutdown") {
    switch (state) {
      case "shutdown_screen":
        return "Shutting down";
      case "delayed_shutdown_screen":
        return "Fatal error, waiting for shutdown";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "reboot") {
    switch (state) {
      case "reboot_screen":
        return "Rebooting";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "startup") {
    switch (state) {
      case "":
        return "Starting up";
      case "press_reset_on_boot_screen":
        return "Operator action needed";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "disinfect zone") {
    switch (state) {
      case "room_selection":
        return "Operator prepares disinfection";
      case "open_door_to_enter":
      case "open_door_to_leave":
        return "Operator action needed";
      case "driving":
        return "Driving autonomously";
      case "driving_manual":
        return "Driving - Remote controlled";
      case "driving_paused":
        return "Driving paused";
      case "driving_failed":
        return "Stopped";
      case "mode_selection":
      case "mode_setup":
      case "checklist":
      case "login_screen":
      case "review_screen":
      case "cover_removal":
      case "press_start_disinfection_button":
      case "leave_room_close_door":
      case "install_safety_devices":
        return "Operator prepares disinfection";
      case "start_disinfection_countdown":
        return "Waiting for disinfection to start";
      case "disinfection_interrupted":
        return "Disinfection interrupted, resuming";
      case "disinfecting_warm_up":
        return "Warming up";
      case "manual_disinfecting":
        return "Disinfecting - Remote controlled mode";
      case "stationary_disinfecting":
        return "Disinfecting - Stationary mode";
      case "interruption_decision":
        return "Disinfection interrupted";
      case "disinfection_report":
        return "Reviewing report";
      case "disinfecting":
        return "Disinfecting - Autonomous mode";
      case "location_selection":
      case "localize":
      case "mount_devices":
      case "safety_statements":
      case "skip_start_button_press":
      case "type_options":
        return "Operator prepares disinfection";
      case "disinfecting_predefined_driving_to_position":
      case "driving_to_exit_position_entrance":
      case "driving_to_exit_position_start":
        return "Driving autonomously";
      case "disinfecting_predefined_on_position":
        return "Disinfecting - Autonomous mode";
      case "disinfecting_remote_controlled":
        return "Disinfecting - Remote controlled mode";
      case "disinfecting_stationary":
        return "Disinfecting - Stationary mode";
      case "disinfecting_interrupted_unsafe":
        return "Disinfection Interrupted";
      case "disinfecting_interrupted_resuming":
        return "Disinfection Interrupted, resuming";
      case "disinfecting_start_countdown":
        return "Waiting for disinfection to start";
      case "enter_manual_control_open_door":
      case "enter_waiting_open_entrance_door":
      case "exit_manual_control_open_door":
      case "exit_waiting_open_entrance_door":
      case "waiting_door_closed":
      case "waiting_start_button_press":
        return "Operator action needed";
      case "failure":
        return "Driving failed";
      case "finished":
      case "report":
      case "report_manual_control":
      case "review":
        return "Disinfection finished";
      case "go_to_dock":
        return "Driving to dock";
      case "cancelled":
        return "Disinfection cancelled";
      case "shutting_down":
        return "Shutting down";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "idle") {
    switch (state) {
      case "init":
      case "waiting_to_charge":
      case "trying_to_charge":
      case "charging":
      case "stop_charging":
      case "charged":
      case "fully_charged":
      case "over_charged":
        return "Docked";
      case "not_docked":
        return "Idle";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "autonomous docking") {
    switch (state) {
      case "going_to_docking_station":
        return "Driving to dock";
      case "going_to_dock_near_target":
        return "Driving paused";
      case "align_with_charging_pad":
      case "check_proximity_to_docking_station":
        return "Docking";
      case "failed_autonomous_movement":
      case "failed":
        return "Docking failed";
      case "cancel":
        return "Docking cancelled";
      case "success":
        return "Docked";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "autonomous undocking") {
    switch (state) {
      case "undocked":
      case "already_undocked":
        return "Idle";
      case "moving":
      case "warning_to_move":
        return "Driving autonomously";
      case "waiting_stop_charging":
      case "still_docked":
        return "Docked";
      case "failed_stop_charging":
      case "movement_failed":
        return "Undocking failed";
      case "cancel_button":
      case "cancel_command":
        return "Undocking cancelled";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "localize") {
    switch (state) {
      case "init":
        return "Locating on map";
      case "driving":
        return "Driving autonomously";
      case "manual_drive":
        return "Driving - Remote controlled";
      case "failed_load_map":
      case "place_on_map":
      case "selected_apply":
      case "selected_exit":
      case "waiting_mode":
        return "Operator prepares disinfection";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "localize") {
    switch (state) {
      case "joystick":
        return "Driving - Remote controlled";
      case "localizing":
        return "Locating on map";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "manual_control") {
    switch (state) {
      case "joystick":
        return "Driving - Remote controlled";
      case "localizing":
        return "Locating on map";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "mapping") {
    switch (state) {
      case "alignment_processing":
      case "alignment_review":
      case "alignment_waiting_decision":
      case "failed_load_map":
      case "finished":
      case "init":
      case "maps_in_bad_state":
      case "selected_exit":
      case "wait_load_map":
      case "wait_localization_ready":
      case "wait_slam_ready":
        return "Map editing";
      case "joystick":
      case "manual_control":
        return "Driving - Remote controlled";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "joystick") {
    switch (state) {
      case "controlling":
      case "waiting_to_accept_connection":
      case "waiting_to_accept_any_connection":
        return "Driving - Remote controlled";
      default:
        return defaultStatusMsg;
    }
  }
  if (dbType === "set map item") {
    switch (state) {
      case "take_picture":
      case "open_camera":
      case "finished":
      case "review_screen":
      case "cancel":
      case "set_name":
      case "set_time":
      case "docking_station":
      case "entrance_position":
      case "start_position":
      case "disinfection_position":
      case "saved_location":
        return "Map editing";
      case "manual_control":
        return "Driving - Manual controlled";
      default:
        return defaultStatusMsg;
    }
  }
};
